import { useMantineTheme } from "@mantine/core"
import {
  ColumnPinningState,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table"
import { CHECKBOX_COLUMN_ID } from "@tfg/ui"
import { useCallback, useLayoutEffect, useMemo } from "react"
import type { Updater } from "@tanstack/react-table"

import { useHeaderControlsContext } from "~/components/contexts/useHeaderControlsContext"

type Props<TData> = {
  initialColumnPinning?: ColumnPinningState
} & Omit<TableOptions<TData>, "getCoreRowModel" | "getSortedRowModel">

export const useCustomReactTable = <TData>({
  initialColumnPinning,
  ...config
}: Props<TData>) => {
  const {
    orderedColumns,
    columnPinning,
    columnVisibility,
    setOrderedColumns,
    setColumnPinning,
    setColumnVisibility,
  } = useHeaderControlsContext()
  const theme = useMantineTheme()

  const initiateColumnsOrder = useCallback(
    (columns: ColumnDef<TData>[]) =>
      columns
        .map((col: ColumnDef<TData>) => {
          const hasIdKey = typeof col === "object" && "id" in col
          const hasAccessorKey = typeof col === "object" && "accessorKey" in col

          if (hasIdKey) {
            return col.id
          }
          if (hasAccessorKey) {
            return String(col.accessorKey).split(".").join("_")
          }
          return
        })
        .filter(Boolean),
    []
  )
  const { state, ...rest } = config

  const defaultColumnOrder = useMemo(
    () => initiateColumnsOrder(config.columns),
    [config.columns, initiateColumnsOrder]
  )

  useLayoutEffect(() => {
    if (orderedColumns.length === 0 && defaultColumnOrder.length > 0) {
      setOrderedColumns(defaultColumnOrder)
    }
  }, [defaultColumnOrder, orderedColumns.length, setOrderedColumns])

  const handleColumnOrderChange = useCallback(
    (updater: Updater<string[]>) => {
      setOrderedColumns((previous) => {
        const next =
          typeof updater === "function" ? updater(previous) : updater
        if (
          previous.length === next.length &&
          previous.every((columnId, index) => columnId === next[index])
        ) {
          return previous
        }
        return next
      })
    },
    [setOrderedColumns]
  )

  const defaultTableConfig = useMemo(
    () => ({
      defaultColumn: {
        size: theme.other.table.headers.size,
        minSize: theme.other.table.headers.minSize,
        maxSize: theme.other.table.headers.maxSize,
      },
      state: {
        columnPinning: columnPinning ?? {
          left: [CHECKBOX_COLUMN_ID, ...(initialColumnPinning?.left || [])],
        },
        columnVisibility,
        columnOrder:
          orderedColumns.length > 0 ? orderedColumns : defaultColumnOrder,
        ...state,
      },
      onColumnPinningChange: setColumnPinning,
      onColumnVisibilityChange: setColumnVisibility,
      onColumnOrderChange: handleColumnOrderChange,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
    }),
    [
      columnPinning,
      columnVisibility,
      orderedColumns,
      defaultColumnOrder,
      state,
      theme,
      initialColumnPinning?.left,
      setColumnPinning,
      setColumnVisibility,
      handleColumnOrderChange,
    ]
  )

  return useReactTable({
    ...defaultTableConfig,
    ...rest,
  })
}
